import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Toast } from "./Toast";

/**
 * Small provider/hook (REDISENO-PANEL.md §2 "Toasts": "Reemplazan los
 * `<p role='status'>` de éxito tras una acción") so a later slice can call
 * `useToast().showToast(message)` instead of rendering its own inline
 * `<p role="status">` success message — this slice only builds the
 * plumbing; no existing success message is migrated yet.
 */
/** Codex findings review (panel-redesign-v3-04, #11): `persistent` skips the
 * auto-dismiss timer (e.g. a successful create whose saveAudienceFilter call
 * then fails -- the operator must not race a timer to read that warning) and
 * `variant` is the purely visual success/warning styling hook, both passed
 * straight through to `<Toast>`. */
interface ShowToastOptions {
  variant?: "success" | "warning";
  persistent?: boolean;
}

interface ToastContextValue {
  showToast: (message: string, options?: ShowToastOptions) => void;
}

interface ActiveToast extends ShowToastOptions {
  id: number;
  message: string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  // Every showToast call gets its own id, even repeats of the same text, so
  // the <Toast> below always gets a fresh `key` -- React remounts it (new
  // DOM node, fresh auto-dismiss timer) instead of bailing out on an
  // unchanged `message` prop. Without this, calling showToast twice with
  // the same text would neither restart the timer nor re-announce the
  // role="status" region to assistive technology.
  const nextIdRef = useRef(0);

  const showToast = useCallback((next: string, options?: ShowToastOptions) => {
    nextIdRef.current += 1;
    setToast({ id: nextIdRef.current, message: next, ...options });
  }, []);

  const dismiss = useCallback(() => setToast(null), []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast !== null && (
        <Toast
          key={toast.id}
          message={toast.message}
          onDismiss={dismiss}
          variant={toast.variant}
          persistent={toast.persistent}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
